import { getServerValidatedEnv } from '../../config/server-env';
import { SanityServiceSimple } from '../../services/SanityServiceSimple/SanityServiceSimple';
import { isSSR } from '../../utils/helpers';
import type { SemboMarket } from '../../utils/markets';
import {
  getCopyQuery,
  imageByRef,
  mediaGroupById,
  slugDefinedInEntriesWithLanguage,
} from './queries';
import type {
  CopyQueryResult,
  GetCopyResult,
  SanityClientFetchOptions,
  SanityImageType,
} from './types';

export const getCopy = async (
  type: 'microcopy' | 'macrocopy',
  filters: { include?: string[]; exclude?: string[] } | undefined,
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<GetCopyResult> => {
  // Disable translations if specified by environment settings (useful for debugging)
  if (isSSR()) {
    const env = getServerValidatedEnv();

    if (env.TRANSLATION_DISABLED) {
      return {};
    }
  }

  const result = await SanityServiceSimple.fetch<CopyQueryResult>(
    getCopyQuery(type, filters, market),
    previewSettings,
    {
      source: 'getCopy',
      params: { market: market.host, type },
    }
  );

  return result.reduce((coll: GetCopyResult, item) => {
    coll[item.key] = item.value;
    return coll;
  }, {});
};

export const getSlugDefinedInEntriesWithLanguage = async (
  slug: string,
  excludeId: string,
  contentType: string,
  path: string,
  previewSettings: SanityClientFetchOptions
): Promise<boolean> => {
  const result = await SanityServiceSimple.fetch<boolean>(
    slugDefinedInEntriesWithLanguage(slug, excludeId, contentType, path),
    previewSettings,
    {
      source: 'getSlugDefinedInEntriesWithLanguage',
      params: { slug, excludeId, contentType, path },
    }
  );

  return result;
};

export const getMediaGroupById = async (
  market: SemboMarket,
  id: string,
  previewSettings: SanityClientFetchOptions
): Promise<SanityImageType[]> => {
  const result = await SanityServiceSimple.fetch<SanityImageType[]>(
    mediaGroupById(market, id),
    previewSettings,
    { source: 'getMediaGroupById', params: { market: market.host, id } }
  );

  if (!result) {
    return [];
  }

  return result;
};

export const getImageByRef = async (
  market: SemboMarket,
  ref: string,
  previewSettings: SanityClientFetchOptions
): Promise<SanityImageType | null> => {
  const result = await SanityServiceSimple.fetch<SanityImageType | null>(
    imageByRef(market, ref),
    previewSettings,
    {
      source: 'getImageByRef',
      params: {
        market: market.host,
        ref,
      },
    }
  );

  return result;
};
